<script setup>
import { ref, onMounted, computed, watch, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import Draggable from 'vuedraggable';
import TicketCard from 'dashboard/components-next/tickets/TicketCard.vue';
import TicketViewModal from '../components/TicketViewModal.vue';
import TicketRegisterPage from './TicketRegisterPage.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import FilterConfiguration from '../components/FilterConfiguration.vue';
import TicketFiltersBar from '../components/TicketFiltersBar.vue';
import { useTicketFilters } from '../composables/useTicketFilters';
import { useKanbanBoards } from '../composables/useKanbanBoards';
import { useKanbanColumns } from '../composables/useKanbanColumns';
import { useKanbanDragAndDrop } from '../composables/useKanbanDragAndDrop';
import { useKanbanModals } from '../composables/useKanbanModals';

const store = useStore();
const router = useRouter();
const route = useRoute();
const tickets = useMapGetter('tickets/getTickets');
const ticketsLoading = computed(
  () => store.getters['tickets/getUIFlags'].isFetching
);

// Use kanban boards composable first so board is available for ticket filters
const {
  board,
  availableBoards,
  showBoardSelector,
  selectedBoardId,
  boardsLoading,
  onBoardChange,
  loadBoards,
  getTeamName,
} = useKanbanBoards();

// Use ticket filters composable (filters tickets by selected board when getBoardId is provided)
const {
  filterListOptions,
  filterData,
  visibleFilters,
  searchQuery,
  searchInputRef,
  selectedFilters,
  ticketAttributes,
  handleSearch,
  getSelectedItems,
  handleItemSelect,
  handleCustomAttributeFilter,
  getCustomAttributeFilterValue,
  removeFilter,
  handleClearFilters,
  refreshTickets,
  initializeFilters,
  loadFilterData,
} = useTicketFilters({
  view: 'kanban',
  storageKey: 'kanban_board',
  excludeTeamFilter: true,
  disablePagination: true,
  getBoardId: () => board.value?.id,
});

// Use kanban drag and drop composable (need to create refs first for isUpdatingFromDrag)
const isUpdatingFromDrag = ref(false);

// Use kanban columns composable
const {
  columns,
  columnTickets,
  updateColumnTickets,
  updateColumnsTickets,
} = useKanbanColumns(board, tickets, isUpdatingFromDrag);

// Use kanban drag and drop composable
const {
  draggedTicketId,
  isDragging,
  handleDragStart,
  handleDragAdd,
  handleColumnChange,
  handleTicketMove,
} = useKanbanDragAndDrop({
  columns,
  board,
  tickets,
  updateColumnTickets,
  updateColumnsTickets,
});

// Use kanban modals composable
const {
  showTicketModal,
  selectedTicketId,
  selectedColumnId,
  openTicketModal,
  closeTicketModal,
  hasNextTicket,
  hasPreviousTicket,
  navigateToNextTicket,
  navigateToPreviousTicket,
  showCreateTicketModal,
  createTicketColumnId,
  openCreateTicketModal,
  closeCreateTicketModal,
  onTicketCreated,
} = useKanbanModals(refreshTickets, columnTickets, router, route, store);

// Ref for TicketFiltersBar component to access its searchInputRef
const ticketFiltersRef = ref(null);

// Wrapper function to open ticket modal with column info
const handleOpenTicketModal = ticketId => {
  // Find which column this ticket belongs to.
  const matchedColumnId = Object.entries(columnTickets.value).find(
    ([, ticketsInColumn]) =>
      ticketsInColumn.some(ticket => ticket.id === ticketId)
  )?.[0];
  const ticketColumnId = matchedColumnId ? Number(matchedColumnId) : null;
  openTicketModal(ticketId, ticketColumnId);
};

const loading = computed(() => ticketsLoading.value || boardsLoading.value);
const isBootstrapping = ref(true);

// Refetch tickets when the selected board changes so the list matches the board
watch(
  () => board.value?.id,
  (newBoardId, oldBoardId) => {
    if (newBoardId && newBoardId !== oldBoardId) {
      refreshTickets();
    }
  }
);

onMounted(async () => {
  try {
    // Bootstrap everything together to avoid staged UI flickering.
    await Promise.allSettled([initializeFilters(), loadFilterData(), loadBoards()]);

    // Update searchInputRef to point to the input in TicketFiltersBar component
    await nextTick();
    if (ticketFiltersRef.value && ticketFiltersRef.value.searchInputRef) {
      searchInputRef.value = ticketFiltersRef.value.searchInputRef;
    }
  } finally {
    isBootstrapping.value = false;
  }
});
</script>

<template>
  <div class="kanban-dashboard">
    <!-- Header with Board Selector -->
    <div class="flex items-center justify-between mb-6">
      <div
        v-if="showBoardSelector"
        class="flex items-center justify-center gap-2"
      >
        <div>
          <label
            for="board-select"
            class="text-base font-medium text-slate-700 dark:text-slate-300"
          >
            {{ $t('TICKETS.BOARD') }}{{ $t('TICKETS.COLON') }}
          </label>
        </div>
        <div class="px-2">
          <select
            id="board-select"
            :value="selectedBoardId"
            :disabled="loading"
            class="my-auto text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            @change="onBoardChange(Number($event.target.value))"
          >
            <option
              v-for="boardOption in availableBoards"
              :key="boardOption.id"
              :value="boardOption.id"
            >
              {{ boardOption.name }} ({{ getTeamName(boardOption.team_id) }})
            </option>
          </select>
        </div>
      </div>

      <div
        v-else-if="!showBoardSelector && board && !loading"
        class="flex items-center justify-center gap-2"
      >
        <div>
          <span
            class="text-base font-medium text-slate-700 dark:text-slate-300"
          >
            {{ $t('TICKETS.BOARD') }}{{ $t('TICKETS.COLON') }}
          </span>
        </div>
        <div class="px-2 text-sm text-slate-900 dark:text-slate-100">
          {{ board.name }} ({{ getTeamName(board.team_id) }})
        </div>
      </div>

      <div class="flex items-center space-x-3">
        <FilterConfiguration
          :filters="filterListOptions"
          :visible-filters="visibleFilters"
          @update:visible-filters="visibleFilters = $event"
        />

        <Button
          color="slate"
          size="sm"
          :disabled="loading"
          class="w-40"
          @click="refreshTickets"
        >
          <svg
            class="h-4 w-4 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          {{ loading ? 'Carregando...' : 'Atualizar' }}
        </Button>
      </div>
    </div>

    <!-- Search and Filters Bar -->
    <TicketFiltersBar
      ref="ticketFiltersRef"
      :loading="loading"
      :filter-list-options="filterListOptions"
      :filter-data="filterData"
      :visible-filters="visibleFilters"
      :ticket-attributes="ticketAttributes"
      :search-query="searchQuery"
      :selected-filters="selectedFilters"
      :get-selected-items="getSelectedItems"
      :handle-item-select="handleItemSelect"
      :handle-custom-attribute-filter="handleCustomAttributeFilter"
      :get-custom-attribute-filter-value="getCustomAttributeFilterValue"
      :remove-filter="removeFilter"
      :handle-clear-filters="handleClearFilters"
      :handle-search="handleSearch"
      search-placeholder="Pesquisar por titulo e descricao..."
      @update:search-query="value => (searchQuery = value)"
    />

    <div class="kanban-board">
      <div v-if="isBootstrapping" class="flex justify-center py-8">
        <div class="text-center">
          <div
            class="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600 mx-auto mb-4"
          />
          <p class="text-slate-500 dark:text-slate-400">
            Carregando colunas...
          </p>
        </div>
      </div>

      <div
        v-else-if="availableBoards.length === 0"
        class="flex justify-center py-8 w-full"
      >
        <div class="text-center">
          <p class="text-slate-500 dark:text-slate-400">
            Não existe kanban configurado para os seus times.
          </p>
        </div>
      </div>

      <div
        v-else-if="columns.length === 0"
        class="flex justify-center py-8 w-full"
      >
        <div class="text-center">
          <p class="text-slate-500 dark:text-slate-400">
            Não existe kanban configurado para o quadro selecionado.
          </p>
        </div>
      </div>

      <div v-else class="kanban-columns-wrapper">
        <div class="kanban-columns">
          <div
            v-for="column in columns"
            :key="column.id"
            class="kanban-column"
            :data-column-id="column.id"
          >
            <div class="column-header">
              <h3
                class="text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                {{ column.name }}
              </h3>
              <span class="badge-count">{{
                (columnTickets[column.id] || []).length
              }}</span>
            </div>
            <div class="column-content" :data-column-id="column.id">
              <div class="relative flex flex-col h-full">
                <Draggable
                  v-if="
                    (columnTickets[column.id] || []).length > 0 || isDragging
                  "
                  :list="columnTickets[column.id] || []"
                  :group="{ name: 'tickets', pull: true, put: true }"
                  :animation="200"
                  ghost-class="opacity-50"
                  drag-class="cursor-grabbing"
                  item-key="id"
                  class="space-y-3 min-h-[400px]"
                  :data-column-id="column.id"
                  @start="handleDragStart"
                  @add="event => handleDragAdd(event, column.id)"
                  @change="event => handleColumnChange(event, column.id)"
                  @end="handleTicketMove"
                >
                  <template #item="{ element: ticket }">
                    <div :data-ticket-id="ticket.id" class="draggable-item">
                      <TicketCard
                        :key="ticket.id"
                        :ticket="ticket"
                        :is-dragging="draggedTicketId === ticket.id"
                        @open="handleOpenTicketModal"
                      />
                    </div>
                  </template>
                </Draggable>
                <div
                  v-if="
                    (columnTickets[column.id] || []).length === 0 && !isDragging
                  "
                  class="empty-state flex-1 flex items-center justify-center"
                >
                  <div class="text-center py-8">
                    <div class="text-slate-400 dark:text-slate-500 mb-2">
                      <svg
                        class="w-8 h-8 mx-auto"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                        />
                      </svg>
                    </div>
                    <p class="text-sm text-slate-500 dark:text-slate-400">
                      Nenhum ticket {{ column.name.toLowerCase() }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div class="column-add-button">
              <Button
                size="sm"
                variant="ghost"
                color="slate"
                icon="i-lucide-plus"
                :label="$t('TICKETS.BUTTONS.ADD_TICKET')"
                class="w-full justify-start"
                @click="openCreateTicketModal(column.id)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Ticket View Modal -->
    <TicketViewModal
      v-if="selectedTicketId"
      v-model:show="showTicketModal"
      :ticket-id="selectedTicketId"
      :column-id="selectedColumnId"
      :has-next="hasNextTicket"
      :has-previous="hasPreviousTicket"
      @close="closeTicketModal"
      @navigate-next="navigateToNextTicket"
      @navigate-previous="navigateToPreviousTicket"
    />

    <!-- Create Ticket Modal -->
    <woot-modal
      v-model:show="showCreateTicketModal"
      :on-close="closeCreateTicketModal"
      modal-type="centered"
      size="medium"
    >
      <div class="flex flex-col h-auto overflow-auto">
        <div class="overflow-hidden flex justify-center items-center">
          <TicketRegisterPage
            v-if="createTicketColumnId && board"
            :board-id="board.id"
            :board-column-id="createTicketColumnId"
            fetch-contacts
            emit-on-create
            @ticket-created="onTicketCreated"
            @cancel="closeCreateTicketModal"
          />
        </div>
      </div>
    </woot-modal>
  </div>
</template>

<style lang="postcss">
.kanban-dashboard {
  @apply p-6 w-full min-w-0;
}

.kanban-board {
  @apply w-full min-w-0;
}

.kanban-columns-wrapper {
  @apply overflow-x-auto min-w-0 w-full;
}

.kanban-columns {
  @apply flex gap-6 pb-2;
  min-width: 100%;
}

.kanban-column {
  @apply bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 flex-shrink-0 w-[320px] flex flex-col;
  max-height: calc(100vh - 200px);
}

.column-header {
  @apply flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700 flex-shrink-0;
}

.badge-count {
  @apply inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200;
}

.column-add-button {
  @apply px-4 py-2 border-t border-slate-200 dark:border-slate-700 flex-shrink-0;
}

.column-content {
  @apply p-4 mb-4 flex-1 overflow-y-auto min-h-0;
}

.empty-state {
  @apply flex items-center justify-center min-h-0;
}
</style>
